import { supabase } from './supabaseClient';
import { assertPropertyAccess } from './propertyAuthorization';

export type GuestPii = {
  name: string | null;
  email: string | null;
};

type DecryptRow = {
  reservation_id: string;
  guest_name: string | null;
  guest_email: string | null;
};

// Upper bound for a single batch. The RPC joins against vault.decrypted_secrets
// per reservation, so an unbounded array argument is effectively a DoS vector.
// 200 covers every display path (dashboard cap is 10; day-panel queries are
// bounded by room inventory, typically 20–80). Analytics/export callers over
// longer windows should paginate rather than bumping this — one failed batch
// of 2000 is much more expensive to retry than four batches of 500.
const MAX_RESERVATION_IDS_PER_CALL = 200;

/**
 * Batch-decrypt guest PII (name, email) for the given reservation ids.
 *
 * Security contract:
 *   1. Caller must be an authenticated user assigned to `propertyId` via
 *      `user_properties` — enforced here (`assertPropertyAccess`) AND at the
 *      RPC (which joins against `user_properties` row-by-row). The double
 *      check is deliberate defense in depth: the TS gate gives a fast fail
 *      and a clear error, the DB gate is the security boundary that survives
 *      any future app-layer refactor.
 *   2. Only reservation ids that belong to `propertyId` are meaningful. Ids
 *      from another property would be silently dropped by the RPC's
 *      ownership join anyway, but we document the intent in the signature.
 *
 * Throws `PropertyAuthorizationError` if the caller isn't entitled to the
 * property — the existing convention in the codebase. RPC errors (network,
 * Vault failure, etc.) are logged and an empty map is returned so the UI
 * degrades to "Unknown Guest" instead of crashing the page.
 */
export async function fetchGuestPiiMap(
  propertyId: string,
  reservationIds: string[]
): Promise<Map<string, GuestPii>> {
  if (typeof propertyId !== 'string' || propertyId.length === 0) {
    throw new Error('fetchGuestPiiMap: propertyId is required');
  }
  if (!Array.isArray(reservationIds)) {
    throw new Error('fetchGuestPiiMap: reservationIds must be an array');
  }
  if (reservationIds.length === 0) return new Map();
  if (reservationIds.length > MAX_RESERVATION_IDS_PER_CALL) {
    throw new Error(
      `fetchGuestPiiMap: too many reservation ids in one call (got ${reservationIds.length}, max ${MAX_RESERVATION_IDS_PER_CALL})`
    );
  }

  // App-layer authorization gate. Matches the pattern used across every
  // other property-scoped service in this codebase. Throws on failure;
  // callers that want to degrade silently can .catch() at the call site.
  await assertPropertyAccess(propertyId);

  // Normalize: reject non-string entries and dedupe so a caller accidentally
  // passing duplicates doesn't inflate the payload size.
  const uniqueIds = Array.from(
    new Set(
      reservationIds.filter(
        (id): id is string => typeof id === 'string' && id.length > 0
      )
    )
  );
  if (uniqueIds.length === 0) return new Map();

  const { data, error } = await supabase.rpc('decrypt_reservation_guest_pii', {
    p_reservation_ids: uniqueIds,
  });

  if (error) {
    console.error('[reservationGuestPii] decrypt RPC failed:', error);
    return new Map();
  }

  const map = new Map<string, GuestPii>();
  for (const row of (data ?? []) as DecryptRow[]) {
    map.set(row.reservation_id, {
      name: row.guest_name,
      email: row.guest_email,
    });
  }
  return map;
}
